import { useCallback, useState } from 'react';
import { Button, Input, Textarea, Toggle, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { CheckIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HtmlTemplateManagerProps {
  templates: Record<string, string>;
  onChange: (templates: Record<string, string>) => void;
  disabled?: boolean;
}

export function HtmlTemplateManager({ templates, onChange, disabled }: HtmlTemplateManagerProps) {
  const { t } = useTranslation('admin');

  const keys = Object.keys(templates);
  const [selectedKey, setSelectedKey] = useState<string | null>(keys[0] ?? null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const handleAdd = useCallback(() => {
    let name = 'new-template';
    let i = 1;
    while (name in templates) {
      name = `new-template-${i++}`;
    }
    onChange({ ...templates, [name]: '' });
    setSelectedKey(name);
    setMode('edit');
  }, [templates, onChange]);

  const handleDelete = useCallback(
    (key: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = templates;
      onChange(rest);
      if (selectedKey === key) {
        const remaining = Object.keys(rest);
        setSelectedKey(remaining[0] ?? null);
      }
    },
    [templates, onChange, selectedKey],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      if (!selectedKey) return;
      onChange({ ...templates, [selectedKey]: value });
    },
    [templates, onChange, selectedKey],
  );

  const startRename = useCallback((key: string) => {
    setRenamingKey(key);
    setRenameValue(key);
  }, []);

  const confirmRename = useCallback(() => {
    if (!renamingKey || !renameValue.trim() || renameValue === renamingKey) {
      setRenamingKey(null);
      return;
    }
    if (renameValue in templates) {
      setRenamingKey(null);
      return;
    }
    const entries = Object.entries(templates).map(([k, v]) =>
      k === renamingKey ? [renameValue, v] : [k, v],
    );
    onChange(Object.fromEntries(entries) as Record<string, string>);
    if (selectedKey === renamingKey) {
      setSelectedKey(renameValue);
    }
    setRenamingKey(null);
  }, [renamingKey, renameValue, templates, onChange, selectedKey]);

  const selectedContent = selectedKey ? (templates[selectedKey] ?? '') : '';

  return (
    <div className="flex flex-col gap-4">
      {/* Template tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {keys.map((key) => (
          <div key={key} className="flex items-center gap-1">
            {renamingKey === key ? (
              <div className="flex items-center gap-1">
                <Input
                  value={renameValue}
                  aria-label={t('admin.htmlTemplateManager.newTemplateName')}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmRename();
                    if (e.key === 'Escape') setRenamingKey(null);
                  }}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className="w-40 text-[13px]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('admin.htmlTemplateManager.confirmRename')}
                  onClick={confirmRename}
                >
                  <CheckIcon />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('admin.htmlTemplateManager.cancelRename')}
                  onClick={() => setRenamingKey(null)}
                >
                  <XIcon />
                </Button>
              </div>
            ) : (
              <>
                <Toggle
                  size="sm"
                  pressed={selectedKey === key}
                  onPressedChange={() => setSelectedKey(key)}
                >
                  {key}
                </Toggle>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('admin.htmlTemplateManager.deleteTemplate', {
                      templateKey: key,
                    })}
                    onClick={() => handleDelete(key)}
                  >
                    <XIcon />
                  </Button>
                )}
              </>
            )}
          </div>
        ))}
        {/* The empty state carries its own labelled Add button, so this one only
            appears once there is a row of templates to append to. */}
        {!disabled && keys.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={t('admin.htmlTemplateManager.addTemplate')}
            onClick={handleAdd}
            className="border border-dashed border-border"
          >
            <PlusIcon />
          </Button>
        )}
      </div>

      {/* Selected template editor */}
      {selectedKey && (
        <div className="flex flex-col gap-3">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-base font-medium leading-6">{selectedKey}</p>
              {!disabled && renamingKey !== selectedKey && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('admin.htmlTemplateManager.renameTemplate', {
                    templateKey: selectedKey,
                  })}
                  onClick={() => startRename(selectedKey)}
                >
                  <PencilIcon />
                </Button>
              )}
            </div>
            <ToggleGroup
              type="single"
              size="sm"
              value={mode}
              aria-label={t('admin.htmlTemplateManager.viewMode')}
              onValueChange={(v) => v && setMode(v as 'edit' | 'preview')}
            >
              {/* eslint-disable i18next/no-literal-string -- toggle option value attributes */}
              <ToggleGroupItem value="edit">{t('admin.htmlTemplateManager.code')}</ToggleGroupItem>
              <ToggleGroupItem value="preview">
                {t('admin.htmlTemplateManager.preview')}
              </ToggleGroupItem>
              {/* eslint-enable i18next/no-literal-string */}
            </ToggleGroup>
          </div>

          {/* Editor / Preview */}
          {mode === 'edit' ? (
            <Textarea
              rows={12}
              value={selectedContent}
              onChange={(e) => handleContentChange(e.target.value)}
              disabled={disabled}
              aria-label={selectedKey}
              placeholder={t('admin.htmlTemplateManager.pasteYourHtmlTemplateHere')}
              className="max-h-150 min-h-60 font-mono text-[13px] leading-relaxed"
            />
          ) : (
            <div className="min-h-125 overflow-hidden rounded border border-border bg-background">
              {selectedContent ? (
                <iframe
                  srcDoc={selectedContent}
                  title={t('admin.htmlTemplateManager.previewTemplate', {
                    templateKey: selectedKey,
                  })}
                  sandbox=""
                  className="block h-[60vh] min-h-125 w-full border-0"
                />
              ) : (
                <div className="flex min-h-125 items-center justify-center text-muted-foreground">
                  <p className="text-sm leading-5">
                    {t('admin.htmlTemplateManager.noContentToPreview')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {keys.length === 0 && (
        <div className="flex flex-col items-center gap-4 py-12 text-muted-foreground">
          <p className="text-sm leading-5">
            {t('admin.htmlTemplateManager.noTemplatesConfiguredYet')}
          </p>
          {!disabled && (
            <Button type="button" variant="outline" onClick={handleAdd}>
              <PlusIcon />
              {t('admin.htmlTemplateManager.addTemplate')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
